import type { ReactNode } from "react"
import React, { useEffect, useMemo } from "react"
import { Link, useLocation, useNavigate } from "@tanstack/react-router"
import { IconTriangleWarningFill18 } from "nucleo-ui-fill-18"
import {
	IconAddressBookOutline18,
	IconArrowCornerUpLeftOutline18,
	IconArrowLeftOutline18,
	IconAtom2Outline18,
	IconBanOutline18,
	IconBookOpen4Outline18,
	IconBugOutline18,
	IconChartActivityOutline18,
	IconBoltLightningOutline18,
	IconArrowRotateAnticlockwiseCheckOutline18,
	IconCircleUserOutline18,
	IconCode2Outline18,
	IconConnectedDots2Outline18,
	IconCreditCardOutline18,
	IconCubeOutline18,
	IconDarkLightOutline18,
	IconDatabaseOutline18,
	IconEnvelopeOutline18,
	IconEnvelopePlusOutline18,
	IconFingerprintOutline18,
	IconFireOutline18,
	IconHistoryOutline18,
	IconImportOutline18,
	IconInboxOutline18,
	IconLanguageOutline18,
	IconLayers2Outline18,
	IconLifeRingOutline18,
	IconLink3Outline18,
	IconMagnifierOutline18,
	IconMarkdownOutline18,
	IconStackPerspectiveOutline18,
	IconMessageSparkleOutline18,
	IconMsgsOutline18,
	IconNotificationOutline18,
	IconOffice3Outline18,
	IconOrbitSparkleOutline18,
	IconPaperclipOutline18,
	IconQuoteOutline18,
	IconReceiptOutline18,
	IconShapes2Outline18,
	IconTableOutline18,
	IconTagOutline18,
	IconUserSettingsOutline18,
	IconUsersOutline18,
	IconWindow2Outline18,
	IconWindowUserOutline18,
	IconXmarkOutline18,
} from "nucleo-ui-outline-18"

import { cn, pro } from "@productlane/lib/src/browser"
import {
	AttioIcon,
	HubspotIconGray,
	IconButton,
	Input,
	IntercomIconGray,
	LinearIconGray,
	PlainLogo,
	Shortcut,
	SlackIconGray,
	Tooltip,
	ZapierIconGray,
} from "@productlane/ui"
import { AiAssistantIcon } from "@productlane/ui/src/icons/project/ai-assistant-icon"
import { CursorIcon } from "@productlane/ui/src/icons/project/cursor-icon"
import { GitHubIcon } from "@productlane/ui/src/icons/project/github-icon"
import { McpIcon } from "@productlane/ui/src/icons/project/mcp-icon"
import { MicrosoftTeamsIcon } from "@productlane/ui/src/icons/project/microsoftteams-icon"
import { SentryIcon } from "@productlane/ui/src/icons/project/sentry-icon"
import { ZendeskIcon } from "@productlane/ui/src/icons/project/zendesk-icon"

import type { NavItem } from "../app/sidebar"
import type { IntegrationKey } from "./integrations/registry"
import { RoleCheck } from "@/components/shared/role-check"
import { useShortcut } from "@/lib"
import { api } from "@/lib/api"
import { useIsPhone } from "@/lib/hooks/use-is-phone"
import { hrefLinkOptions } from "@/lib/href-link"
import { useRequiredPlans } from "@/lib/plans"
import { useLinkPress } from "@/lib/press-to-navigate"
import { useRole } from "@/lib/useRole"
import { useZeroWorkspace } from "@/lib/zero"
import {
	ExperimentalTag,
	WideDesktopSidebar as MainAppSidebar,
	useNavigationShortcuts,
	useSwitchWorkspaceShortcut,
} from "../app/sidebar"
import { MOBILE_BAR_CLEARANCE } from "../app/sidebar-geometry"
import { visibleIntegrationNavItems } from "./integrations/nav-visibility"
import {
	RECONNECT_TOOLTIP,
	useIntegrationReconnects,
	useSentryAvailable,
} from "./integrations/registry"
import {
	NAV_ROW_ICON_GAP_CLASS,
	NAV_ROW_ICON_SIZE_CLASS,
	NAV_ROW_PADDING_LEFT_CLASS,
	NAV_ROW_VERTICAL_PADDING_CLASS,
	SEARCH_HEIGHT_CLASS,
	SEARCH_ICON_POSITION_CLASS,
	SEARCH_SUFFIX_SLOT_CLASS,
	SEARCH_TEXT_INSET_CLASS,
} from "./settings-nav-geometry"
import { settingsReturnPath } from "./settings-return-path"
import { isViewerSettingsHref } from "./viewer-settings-access"

const SearchIcon = (props: React.SVGProps<SVGSVGElement>) => (
	<IconMagnifierOutline18 {...props} strokeWidth={1} />
)

export const WORKSPACE_NAV_ITEMS: NavItem[] = [
	{
		href: "/settings/general",
		icon: IconOffice3Outline18,
		title: "General",
	},
	{
		href: "/settings/plans",
		icon: IconCreditCardOutline18,
		title: "Billing",
	},
	{
		href: "/settings/members",
		icon: IconUsersOutline18,
		title: "Members",
	},
	{
		href: "/settings/transfer",
		icon: IconImportOutline18,
		title: "Import / Export",
	},
	{
		href: "/settings/integrations/api",
		icon: IconCode2Outline18,
		title: "API",
	},
	{
		href: "/settings/integrations",
		icon: IconShapes2Outline18,
		title: "Integrations",
	},
]

export const ACCOUNT_NAV_ITEMS: NavItem[] = [
	{
		href: "/settings/notifications",
		icon: IconNotificationOutline18,
		title: "Notifications",
	},
	{
		href: "/settings/mcp",
		icon: McpIcon,
		title: "MCP",
	},
]

export const SETUP_NAV_ITEMS: NavItem[] = [
	{
		href: "/settings/ai",
		icon: IconAtom2Outline18,
		title: "AI",
	},
	{
		href: "/settings/docs",
		icon: IconBookOpen4Outline18,
		title: "Docs",
	},
	{
		href: "/settings/widget",
		icon: IconMsgsOutline18,
		title: "Widget",
	},
	{
		href: "/settings/portal",
		icon: IconWindowUserOutline18,
		title: "Portals",
	},
	{
		href: "/settings/emails",
		icon: IconEnvelopePlusOutline18,
		title: "Email",
	},
	{
		href: "/settings/roadmap",
		icon: IconCubeOutline18,
		title: "Feature requests",
	},
	{
		href: "/settings/changelog",
		icon: IconStackPerspectiveOutline18,
		title: "Changelog",
	},
	{
		href: "/settings/support-portal",
		icon: IconLifeRingOutline18,
		title: "Support Portal",
	},
]

export const INTEGRATIONS_NAV_ITEMS: NavItem[] = [
	{
		href: "/settings/integrations/linear",
		icon: LinearIconGray,
		title: "Linear",
	},
	{
		href: "/settings/integrations/slack",
		icon: SlackIconGray,
		title: "Slack",
	},
	{
		href: "/settings/integrations/teams",
		icon: MicrosoftTeamsIcon,
		title: "Teams",
	},
	{
		href: "/settings/integrations/github",
		icon: GitHubIcon,
		title: "GitHub",
	},
	{
		href: "/settings/integrations/zapier",
		icon: ZapierIconGray,
		title: "Zapier",
	},
	{
		href: "/settings/integrations/plain",
		icon: PlainLogo,
		title: "Plain",
	},
	{
		href: "/settings/integrations/hubspot",
		icon: HubspotIconGray,
		title: "Hubspot",
	},
	{
		href: "/settings/integrations/attio",
		icon: AttioIcon,
		title: "Attio",
	},

	{
		href: "/settings/integrations/postgres",
		icon: IconDatabaseOutline18,
		title: "PostgreSQL",
	},
	{
		href: "/settings/integrations/intercom",
		icon: IntercomIconGray,
		title: "Intercom migrator",
	},
	{
		href: "/settings/integrations/cursor",
		icon: CursorIcon,
		title: "Cursor",
	},
	{
		href: "/settings/integrations/sentry",
		icon: SentryIcon,
		title: "Sentry",
	},
]

export const FEATURES_NAV_ITEMS: NavItem[] = [
	{
		href: "/settings/office-hours",
		icon: IconFireOutline18,
		title: "Office hours & SLAs",
	},
	{
		href: "/settings/languages",
		icon: IconLanguageOutline18,
		title: "Multilingual",
	},
	{
		href: "/settings/custom-connectors",
		icon: IconConnectedDots2Outline18,
		title: "Connectors",
	},
	{
		href: "/settings/macros",
		icon: IconQuoteOutline18,
		title: "Macros",
	},
	{
		href: "/settings/assignments",
		icon: IconUserSettingsOutline18,
		title: "Assignments",
	},
	{
		href: "/settings/tags",
		icon: IconTagOutline18,
		title: "Tags",
	},
]

// Extra search terms per settings page so the sidebar search surfaces
// sub-settings that aren't nav titles (e.g. "sso" -> Portal).
const SETTINGS_SEARCH_KEYWORDS: Record<string, string> = {
	"/settings/general": "logo workspace name delete workspace",
	"/settings/plans":
		"billing subscription upgrade plan payment invoice trial renewal",
	"/settings/members": "invite role permissions allowed domains seats",
	"/settings/transfer":
		"csv import export contacts docs changelog zendesk productboard attachments",
	"/settings/integrations/api":
		"api key workspace id webhooks tokens developer",
	"/settings/integrations":
		"integrations connect apps linear slack teams github zapier plain hubspot attio postgresql intercom cursor",
	"/settings/notifications":
		"inbox email slack notifications thread replies alerts subscriptions",
	"/settings/mcp":
		"mcp model context protocol claude code cursor chatgpt oauth server agent tools",
	"/settings/widget":
		"widget branding help center live chat custom links contact avatars",
	"/settings/portal":
		"portal theme custom domain sso saml single sign-on branding google analytics posthog multiple portals instances slug",
	"/settings/emails":
		"shared email setup blocklist auto-reply forwarding postmark dns",
	"/settings/office-hours":
		"office hours response time timezone schedule availability sla service level agreement priority conditions targets",
	"/settings/languages":
		"multilingual language translation default language locales",
	"/settings/custom-connectors":
		"custom connector data sidebar sections fields api endpoint mcp model context protocol actions tools",
	"/settings/macros": "canned response snippet quick reply template",
	"/settings/assignments":
		"auto-assign linear owner company grouping domain routing",
	"/settings/tags": "tags tag groups categorization labels",
	"/settings/integrations/linear":
		"linear issues sync connect suggestions similar",
	"/settings/integrations/slack": "slack channel notifications connect",
	"/settings/integrations/teams": "microsoft teams notifications",
	"/settings/integrations/zapier": "zapier automation workflow",
	"/settings/integrations/hubspot":
		"hubspot crm sync notes contacts companies push",
	"/settings/integrations/attio": "attio crm sync companies contacts",
	"/settings/integrations/plain": "plain customer data",
	"/settings/integrations/postgres":
		"postgresql database connection sql data access",
	"/settings/integrations/intercom": "intercom customer data",
	"/settings/integrations/cursor": "cursor coding agent code generation",
	"/settings/integrations/sentry": "sentry errors issues monitoring",
	"/settings/integrations/github": "github repository commits docs proposals",
	"/settings/ai":
		"ai agent productlane agent assistant copilot auto-reply propose docs custom instructions welcome message monthly cap drafts custom prompt self-updating help center suggestions changelog",
	"/settings/docs":
		"help center documentation articles publish internal private visibility",
	"/settings/roadmap":
		"feature requests linear public roadmap visibility weekly email survey",
	"/settings/changelog": "changelog publish categories in-app notifications",
	"/settings/support-portal": "support portal customer messages visibility",
}

// Navigable subpages, keyed by their parent nav item's href. Surfaced as
// indented results only while searching, never in the default sidebar.
const SETTINGS_SUBPAGES: Record<string, NavItem[]> = {
	"/settings/integrations/api": [
		{
			href: "/settings/integrations/api/requests",
			icon: IconHistoryOutline18,
			title: "Requests",
		},
	],
	"/settings/plans": [
		{
			href: "/settings/plans/pricing",
			icon: IconLayers2Outline18,
			title: "All plans",
		},
		{
			href: "/settings/plans/invoices",
			icon: IconReceiptOutline18,
			title: "Invoices",
		},
	],
	"/settings/ai": [
		{
			href: "/settings/ai/usage",
			icon: IconChartActivityOutline18,
			title: "Usage",
		},
		{
			href: "/settings/ai/agent",
			icon: IconOrbitSparkleOutline18,
			title: "Productlane Agent",
		},
		{
			href: "/settings/ai/assistant",
			icon: AiAssistantIcon,
			title: "AI Assistant",
		},
		{
			href: "/settings/ai/close-loop",
			icon: IconArrowRotateAnticlockwiseCheckOutline18,
			title: "Close loop",
		},
		{
			href: "/settings/ai/routines",
			icon: IconBoltLightningOutline18,
			title: "Routines",
		},
		{
			href: "/settings/ai/drafts",
			icon: IconMessageSparkleOutline18,
			title: "AI Drafts",
		},
		{
			href: "/settings/ai/bug-inspect",
			icon: IconBugOutline18,
			title: "Bug Inspect",
		},
	],
	"/settings/transfer": [
		{
			href: "/settings/csv-import",
			icon: IconTableOutline18,
			title: "Import conversations from CSV",
		},
		{
			href: "/settings/zendesk-import",
			icon: ZendeskIcon,
			title: "Import from Zendesk",
		},
		{
			href: "/settings/intercom-import",
			icon: IntercomIconGray,
			title: "Import from Intercom",
		},
		{
			href: "/settings/productboard-import",
			icon: IconImportOutline18,
			title: "Import from Productboard",
		},
		{
			href: "/settings/contacts-csv-import",
			icon: IconAddressBookOutline18,
			title: "Import contacts from CSV",
		},
		{
			href: "/settings/changelog-csv-import",
			icon: IconStackPerspectiveOutline18,
			title: "Import changelog from CSV",
		},
		{
			href: "/settings/docs-csv-import",
			icon: IconBookOpen4Outline18,
			title: "Import docs from CSV",
		},
		{
			href: "/settings/docs-markdown-import",
			icon: IconMarkdownOutline18,
			title: "Import docs from Markdown",
		},
		{
			href: "/settings/attachment-import",
			icon: IconPaperclipOutline18,
			title: "Import attachments",
		},
	],
	"/settings/portal": [
		{
			href: "/settings/portal/theme",
			icon: IconDarkLightOutline18,
			title: "Theme",
		},
		{
			href: "/settings/portal/domain",
			icon: IconLink3Outline18,
			title: "Custom domain",
		},
		{
			href: "/settings/portal/sso",
			icon: IconFingerprintOutline18,
			title: "SSO",
		},
		{
			href: "/settings/portal/instances",
			icon: IconWindow2Outline18,
			title: "All portals",
		},
	],
	"/settings/notifications": [
		{
			href: "/settings/notifications/inbox",
			icon: IconInboxOutline18,
			title: "Inbox notifications",
		},
		{
			href: "/settings/notifications/email",
			icon: IconEnvelopeOutline18,
			title: "Email notifications",
		},
		{
			href: "/settings/notifications/slack",
			icon: SlackIconGray,
			title: "Slack notifications",
		},
	],
	"/settings/custom-connectors": [
		{
			href: "/settings/custom-connectors/history",
			icon: IconHistoryOutline18,
			title: "Tool call history",
		},
	],
	"/settings/emails": [
		{
			href: "/settings/emails/setup",
			icon: IconEnvelopeOutline18,
			title: "Email setup",
		},
		{
			href: "/settings/emails/blocklist",
			icon: IconBanOutline18,
			title: "Blocklist",
		},
		{
			href: "/settings/emails/forwarding",
			icon: IconArrowCornerUpLeftOutline18,
			title: "Forwarded emails",
		},
	],
}

// Subpage href to its parent's href. The settings layout derives the back link
// from this, since a subpage's path is not always nested under its parent.
export const SETTINGS_SUBPAGE_PARENT_BY_HREF: Record<string, string> =
	Object.fromEntries(
		Object.entries(SETTINGS_SUBPAGES).flatMap(([parentHref, items]) =>
			items.map((item) => [item.href, parentHref]),
		),
	)

const SETTINGS_SUBPAGE_KEYWORDS: Record<string, string> = {
	"/settings/integrations/api/requests":
		"api requests history log calls mcp rest failed errors status filter",
	"/settings/custom-connectors/history":
		"mcp tool call history audit log arguments parameters agent connector variant guard inbound outbound",
	"/settings/plans/pricing":
		"all plans pricing compare upgrade downgrade starter pro scale features",
	"/settings/plans/invoices":
		"invoices receipts billing history payments stripe pdf",
	"/settings/ai/usage": "ai usage tokens cost spend billing limit resolutions",
	"/settings/ai/agent":
		"ai agent productlane agent support widget live chat docs resolve autoreply welcome message tone knowledge",
	"/settings/ai/assistant":
		"ai assistant copilot in-thread research draft sources linear github postgres",
	"/settings/ai/close-loop":
		"close loop closing the loop follow-up shipped released completed linear issue project upvote notify customer template",
	"/settings/ai/routines":
		"routines automation automations triage auto tag label assign importance priority translate rules workflow",
	"/settings/ai/drafts":
		"ai drafts composer draft reply prepopulate background suggestions",
	"/settings/ai/bug-inspect":
		"bug inspect bug report reproduce code github repository fix pull request draft pr engineering agent sandbox",
	"/settings/csv-import":
		"import conversations threads csv upload spreadsheet migrate",
	"/settings/zendesk-import":
		"import zendesk tickets migrate conversations threads",
	"/settings/intercom-import":
		"import intercom conversations migrate help center articles fin",
	"/settings/productboard-import":
		"import productboard notes features migrate feedback",
	"/settings/contacts-csv-import":
		"import contacts people csv upload spreadsheet migrate",
	"/settings/changelog-csv-import":
		"import changelog entries csv upload spreadsheet migrate",
	"/settings/docs-csv-import":
		"import docs help center articles csv upload spreadsheet migrate",
	"/settings/docs-markdown-import":
		"import docs help center articles markdown md files migrate url site website scrape crawl intercom zendesk gitbook",
	"/settings/attachment-import":
		"import attachments files images migrate rehost",
	"/settings/portal/theme": "theme colors cover image branding",
	"/settings/portal/domain": "custom domain cname url",
	"/settings/portal/sso": "sso saml single sign-on authentication login",
	"/settings/portal/instances": "portals instances multiple slug root",
	"/settings/notifications/inbox":
		"inbox in-app notifications bell desktop browser subscriptions ai drafts filters views",
	"/settings/notifications/email":
		"email notifications personal alerts replies mentions digest",
	"/settings/notifications/slack":
		"slack notifications personal dm direct message alerts connect",
	"/settings/emails/setup":
		"email setup support address sending receiving postmark dns",
	"/settings/emails/blocklist": "blocklist blocked senders spam ignore",
	"/settings/emails/forwarding":
		"forwarded forwarding attribution sender contact automatic forwarder",
}

export const PROFILE_NAV_ITEM: NavItem = {
	href: "/settings/profile",
	icon: IconCircleUserOutline18,
	title: "Profile",
}

// Every settings page and subpage, keywords attached. cmd+k builds its Settings
// group from these two lists, so a page added to a sidebar group above is
// searchable in both surfaces without a second hand-maintained copy.
export const ALL_SETTINGS_NAV_ITEMS: NavItem[] = [
	PROFILE_NAV_ITEM,
	...WORKSPACE_NAV_ITEMS,
	...ACCOUNT_NAV_ITEMS,
	...SETUP_NAV_ITEMS,
	...FEATURES_NAV_ITEMS,
	...INTEGRATIONS_NAV_ITEMS,
].map((item) => ({ ...item, keywords: SETTINGS_SEARCH_KEYWORDS[item.href] }))

export const ALL_SETTINGS_SUBPAGE_NAV_ITEMS: NavItem[] = Object.values(
	SETTINGS_SUBPAGES,
)
	.flat()
	.map((item) => ({ ...item, keywords: SETTINGS_SUBPAGE_KEYWORDS[item.href] }))

export const SettingsSidebar = React.memo(({ content }: SidebarProps) => {
	useNavigationShortcuts()
	useSwitchWorkspaceShortcut()
	const isPhone = useIsPhone()
	return (
		<div className="flex flex-1 bg-gray-100 dark:bg-gray-950">
			{/* Phones only, where it is the bottom bar. On desktop the settings nav and its back link are enough. */}
			{isPhone && <MainAppSidebar />}

			<div className={cn("flex h-dvh w-full", MOBILE_BAR_CLEARANCE)}>
				<div className="relative flex h-full flex-1 grow flex-col overflow-hidden">
					<div className="flex flex-1 overflow-hidden">
						<WideDesktopSidebar />

						<div className="flex h-full max-h-full w-full flex-1 flex-col overflow-hidden">
							{content}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
})

interface SidebarProps {
	content: ReactNode
}

// Hrefs of the always-visible nav items, used to resolve which single item is
// active. Integration detail pages and subpages deliberately aren't listed so
// they fall back to their parent (e.g. /settings/integrations/linear highlights
// "Integrations", not the sibling "API" that shares the /integrations prefix).
const MATCHABLE_HREFS: string[] = [
	"/settings/profile",
	...[
		...WORKSPACE_NAV_ITEMS,
		...ACCOUNT_NAV_ITEMS,
		...SETUP_NAV_ITEMS,
		...FEATURES_NAV_ITEMS,
	].map((item) => item.href),
]

// The active item is the longest href that equals or is a path-prefix of the
// current pathname, so /settings/integrations/api beats /settings/integrations.
function useNavItemMatch(href: string): boolean {
	const { pathname } = useLocation()
	const activeHref = useMemo(() => {
		let best: string | null = null
		for (const candidate of MATCHABLE_HREFS) {
			if (pathname === candidate || pathname.startsWith(candidate + "/")) {
				if (!best || candidate.length > best.length) best = candidate
			}
		}
		return best
	}, [pathname])
	return href === activeHref
}

export const NavItemComponent = React.memo(
	(props: NavItem & { isSubpage?: boolean; warning?: string }) => {
		const match = useNavItemMatch(props.href)
		const linkPress = useLinkPress(props.href)
		const Icon = useMemo(() => {
			return props.icon
		}, [props.icon])
		return (
			<Link
				to={props.href}
				{...linkPress}
				activeOptions={{ exact: true, includeSearch: false }}
				className={cn(
					"flex items-center rounded-md pr-2",
					NAV_ROW_VERTICAL_PADDING_CLASS,
					props.isSubpage ? "pl-8" : NAV_ROW_PADDING_LEFT_CLASS,
					match
						? "bg-gray-200 dark:bg-gray-800"
						: "bg-transparent hover:bg-gray-150 hover:dark:bg-gray-800",
					"group/navlink w-full",
				)}
			>
				{props.warning ? (
					<Tooltip label={props.warning}>
						<span
							aria-label={props.warning}
							className={cn("flex flex-none", NAV_ROW_ICON_GAP_CLASS)}
						>
							<IconTriangleWarningFill18
								className={cn(
									"flex-none text-yellow-500",
									NAV_ROW_ICON_SIZE_CLASS,
								)}
							/>
						</span>
					</Tooltip>
				) : (
					<Icon
						strokeWidth={1}
						className={cn(
							"flex items-center justify-center text-gray-800 group-hover/navlink:text-primary",
							NAV_ROW_ICON_GAP_CLASS,
							NAV_ROW_ICON_SIZE_CLASS,
							match ? "dark:text-gray-200" : "dark:text-gray-400",
						)}
					/>
				)}
				<div
					className={cn(
						"flex-1 select-none truncate text-left text-sm font-medium group-hover/navlink:text-primary",
						match ? "text-primary" : "text-secondary dark:text-gray-400",
					)}
				>
					{props.title}
				</div>
				{props.experimental && <ExperimentalTag />}
			</Link>
		)
	},
)

const WideDesktopSidebar = React.memo(() => {
	return (
		<div className="group hidden min-w-[15.1rem] max-w-[15.1rem] flex-1 shrink-0 border-r border-gray-300 bg-gray-100 md:!block dark:border-gray-700 dark:bg-gray-950">
			<SidebarContent />
		</div>
	)
})

/** A phone has no settings sidebar, so /settings serves the same list as a page. */
export const SettingsMenuScreen = React.memo(() => (
	<div className="flex flex-col">
		{/* Same 10px inset the search band and the nav groups use, so the three
		    left edges line up down the page. */}
		<div className="flex h-12 shrink-0 items-center border-b border-gray-200 px-1 md:px-2.5 dark:border-gray-700/80">
			<h1 className="pl-2 text-base font-semibold text-primary">Settings</h1>
		</div>
		<SidebarContent asPage />
	</div>
))

interface SidebarContentProps {
	asPage?: boolean
}

/** As a page it rides the settings scroll container; a second scroller inside it scrolls twice on a phone. */
const SidebarContent = React.memo(({ asPage }: SidebarContentProps) => {
	const backHref = settingsReturnPath()
	const backPress = useLinkPress(backHref)
	const navigate = useNavigate()
	const { role } = useRole()

	useShortcut("esc", () => {
		void navigate({ href: settingsReturnPath() })
	})

	const isProPlan = useRequiredPlans(pro.andUp)
	const workspace = useZeroWorkspace()
	const { data: info } = api.workspace.info.useQuery(undefined, {
		staleTime: 60_000,
	})
	const cursorConnected = !!info?.cursorConnected
	const sentryAvailable = useSentryAvailable()

	const workspaceNavItems = useMemo(() => {
		const items = [...WORKSPACE_NAV_ITEMS]
		if (role === "VIEWER") {
			return items.filter(
				({ href }) =>
					href === "/settings/general" ||
					href === "/settings/members" ||
					href === "/settings/plans",
			)
		}

		return items
	}, [role])
	const accountNavItems = useMemo(() => {
		return [...ACCOUNT_NAV_ITEMS].filter((item) => {
			if (role === "VIEWER") {
				return item.title !== "Calendar" && item.title !== "Email"
			}

			if (!isProPlan.passes) {
				return item.title !== "Email"
			}

			return true
		})
	}, [role, isProPlan])
	const setupNavItems = useMemo(() => {
		return [...SETUP_NAV_ITEMS]
	}, [])

	const integrationsNavItems = useMemo(
		() =>
			visibleIntegrationNavItems(INTEGRATIONS_NAV_ITEMS, {
				isAdmin: role === "ADMIN",
				plainConnected: !!workspace?.plainSettings,
				intercomConnected: !!workspace?.intercomSettings,
				cursorConnected,
				sentryAvailable,
			}),
		[role, workspace, cursorConnected, sentryAvailable],
	)

	const reconnects = useIntegrationReconnects()
	const reconnectWarning = (href: string) => {
		const key = href.replace("/settings/integrations/", "") as IntegrationKey
		const needed =
			href === "/settings/integrations"
				? Object.values(reconnects).some(Boolean)
				: !!reconnects[key]
		return needed ? RECONNECT_TOOLTIP : undefined
	}
	const featuresNavItems = FEATURES_NAV_ITEMS
	const { pathname } = useLocation()
	useEffect(() => {
		// forward to profile settings if viewer tries to access other settings
		if (role === "VIEWER" && !isViewerSettingsHref(pathname)) {
			void navigate({ to: "/settings/profile", replace: true })
		}
	}, [navigate, pathname, role])

	const [query, setQuery] = React.useState("")
	const searchRef = React.useRef<HTMLInputElement>(null)
	const isSearching = query.trim().length > 0

	useEffect(() => {
		const focus = () => searchRef.current?.focus()
		window.addEventListener("focus-settings-search", focus)
		return () => window.removeEventListener("focus-settings-search", focus)
	}, [])
	const q = query.trim().toLowerCase()
	const matchesQuery = (item: NavItem) => {
		if (item.title.toLowerCase().includes(q)) return true
		return SETTINGS_SEARCH_KEYWORDS[item.href]?.includes(q) ?? false
	}
	const matchesSubpage = (sub: NavItem) => {
		if (sub.title.toLowerCase().includes(q)) return true
		return SETTINGS_SUBPAGE_KEYWORDS[sub.href]?.includes(q) ?? false
	}
	const subpagesOf = (href: string) => SETTINGS_SUBPAGES[href] ?? []
	// Subpages shown while searching: all of them when the parent title matches,
	// otherwise only the ones that match the query themselves.
	const subpagesFor = (item: NavItem): NavItem[] => {
		if (!isSearching) return []
		const titleMatch = item.title.toLowerCase().includes(q)
		return subpagesOf(item.href).filter(
			(sub) => titleMatch || matchesSubpage(sub),
		)
	}
	const itemVisible = (item: NavItem) =>
		matchesQuery(item) || subpagesOf(item.href).some(matchesSubpage)

	const filteredAccount = accountNavItems.filter(itemVisible)
	const filteredWorkspace = workspaceNavItems.filter(itemVisible)
	const filteredFeatures = featuresNavItems.filter(itemVisible)
	const filteredSetup = setupNavItems.filter(itemVisible)
	const filteredIntegrations = integrationsNavItems.filter(itemVisible)
	const hasResults =
		filteredAccount.length > 0 ||
		filteredWorkspace.length > 0 ||
		filteredFeatures.length > 0 ||
		filteredSetup.length > 0 ||
		filteredIntegrations.length > 0

	const renderNavItems = (items: NavItem[]) =>
		items.map((item) => (
			<React.Fragment key={item.href}>
				<NavItemComponent {...item} warning={reconnectWarning(item.href)} />
				{subpagesFor(item).map((sub) => (
					<NavItemComponent
						key={sub.href}
						{...sub}
						isSubpage
						warning={reconnectWarning(sub.href)}
					/>
				))}
			</React.Fragment>
		))

	return (
		<div
			className={cn(
				"flex w-full flex-col items-start",
				!asPage &&
					"h-full max-h-dvh overflow-y-auto overscroll-y-contain pb-20",
			)}
		>
			<div className="sticky top-0 z-10 w-full bg-gray-100 px-1 pb-4 pt-2.5 md:px-2.5 dark:bg-gray-950">
				<Link
					{...hrefLinkOptions(backHref)}
					{...backPress}
					className={cn(
						"group/navlink mb-2.5 flex w-full items-center rounded-md pr-2 hover:bg-gray-150 hover:dark:bg-gray-800",
						NAV_ROW_PADDING_LEFT_CLASS,
						NAV_ROW_VERTICAL_PADDING_CLASS,
					)}
				>
					<IconArrowLeftOutline18
						strokeWidth={1}
						className={cn(
							"flex-none transform-gpu text-tertiary group-hover/navlink:text-primary",
							NAV_ROW_ICON_GAP_CLASS,
							NAV_ROW_ICON_SIZE_CLASS,
						)}
					/>
					<span className="flex-1 select-none truncate text-left text-xs font-normal text-tertiary group-hover/navlink:text-primary">
						Back to app
					</span>
				</Link>
				<Input
					ref={searchRef}
					name="settings-search"
					variant="borderless"
					inputClassName={cn(
						"rounded-full !bg-white text-sm shadow-card dark:!bg-gray-800 dark:shadow-card-dark",
						"focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-600",
						SEARCH_HEIGHT_CLASS,
						SEARCH_TEXT_INSET_CLASS,
					)}
					iconClassName={cn(
						"text-gray-800 dark:text-gray-400",
						SEARCH_ICON_POSITION_CLASS,
					)}
					placeholder="Search settings"
					icon={SearchIcon}
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onKeyDown={(e) => {
						if (e.key !== "Escape") return
						e.preventDefault()
						if (query) setQuery("")
						else e.currentTarget.blur()
					}}
					suffix={
						<span className={SEARCH_SUFFIX_SLOT_CLASS}>
							{isSearching ? (
								<IconButton
									variant="ghost"
									size="sm"
									Icon={IconXmarkOutline18}
									aria-label="Clear search"
									onClick={() => setQuery("")}
								/>
							) : (
								<Shortcut variant="ghost" live={false}>
									/
								</Shortcut>
							)}
						</span>
					}
				/>
			</div>

			{(!isSearching || filteredWorkspace.length > 0) && (
				<div className="flex w-full flex-col gap-0.5 pl-1 pr-1 pt-3 md:pl-2.5 md:pr-2.5">
					<div className="pb-1 pl-2 text-xs font-normal text-tertiary">
						Administration
					</div>
					{renderNavItems(filteredWorkspace)}
				</div>
			)}

			{(!isSearching || filteredAccount.length > 0) && (
				<div className="mt-4 flex w-full flex-col gap-0.5 pl-1 pr-1 md:pl-2.5 md:pr-2.5">
					<div className="pb-1 pl-2 text-xs font-normal text-tertiary">
						Personal
					</div>
					{!isSearching && <NavItemComponent {...PROFILE_NAV_ITEM} />}

					{renderNavItems(filteredAccount)}
				</div>
			)}

			<RoleCheck not={["VIEWER"]}>
				{(!isSearching || filteredSetup.length > 0) && (
					<div className="mt-4 flex w-full flex-col gap-0.5 pl-1 pr-1 md:pl-2.5 md:pr-2.5">
						<div className="pb-1 pl-2 text-xs font-normal text-tertiary">
							Setup
						</div>

						{renderNavItems(filteredSetup)}
					</div>
				)}
			</RoleCheck>

			<RoleCheck not={["VIEWER"]}>
				{(!isSearching || filteredFeatures.length > 0) && (
					<div className="mt-4 flex w-full flex-col gap-0.5 pl-1 pr-1 md:pl-2.5 md:pr-2.5">
						<div className="pb-1 pl-2 text-xs font-normal text-tertiary">
							Features
						</div>

						{renderNavItems(filteredFeatures)}
					</div>
				)}
			</RoleCheck>

			<RoleCheck not={["VIEWER"]}>
				{isSearching && filteredIntegrations.length > 0 && (
					<div className="mt-4 flex w-full flex-col gap-0.5 pl-1 pr-1 md:pl-2.5 md:pr-2.5">
						<div className="pb-1 pl-2 text-xs font-normal text-tertiary">
							Integrations
						</div>
						{renderNavItems(filteredIntegrations)}
					</div>
				)}
			</RoleCheck>

			{isSearching && !hasResults && (
				<div className="mt-4 w-full pl-3 pr-3 text-sm text-tertiary md:pl-[1.125rem] md:pr-[1.125rem]">
					No settings match &quot;{query.trim()}&quot;
				</div>
			)}
		</div>
	)
})
